/**
 * Layout tab: how this lesson's room is arranged in the XR player.
 *
 * Edits go into the draft like every other tab, so an associate's change is a
 * version that waits for approval, and an empty layout means "use the default".
 */

import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { LayoutTemplate, RotateCcw } from 'lucide-react';

import { Button } from '@/Components/ui/button';
import { Label } from '@/Components/ui/label';
import { db } from '../../../config/firebase';
import { useLessonDraftStore } from '../../../stores/lessonDraftStore';
import { LayoutEditor, type LayoutValue } from '../layout/LayoutEditor';
import { classNumberOf } from '../../../lib/three/roomTheme';

interface Props {
  bundle?: {
    assets3d?: Array<{ id?: string; name?: string }>;
    chapter?: { subject?: string; class?: number | string; class_name?: string } | null;
  } | null;
  isReadOnly?: boolean;
  chapterId?: string;
  topicId?: string;
  /** 0-based position of the topic in its chapter: each topic gets its own pieces. */
  topicIndex?: number;
}

export function LayoutTab({ bundle, isReadOnly, chapterId, topicId, topicIndex }: Props) {
  const layout = useLessonDraftStore((s) => s.draftSnapshot?.layout) as LayoutValue | undefined;
  const hasDraft = useLessonDraftStore((s) => !!s.draftSnapshot);
  const [globalDefault, setGlobalDefault] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    getDoc(doc(db, 'app_settings', 'lesson_layout'))
      .then((snap) => {
        if (live && snap.exists()) setGlobalDefault(snap.data());
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const assets = useMemo(
    () =>
      (bundle?.assets3d ?? [])
        .filter((a): a is { id: string; name?: string } => typeof a.id === 'string' && a.id.length > 0)
        .map((a, i) => ({ id: a.id, name: a.name || `Model ${i + 1}` })),
    [bundle]
  );

  const value = layout ?? {};
  const inheriting = Object.keys(value).length === 0;
  const onChange = (next: LayoutValue) => useLessonDraftStore.getState().updateTab('layout', next);

  if (!hasDraft) {
    return <div className="p-6 text-sm text-muted-foreground">Select a topic to edit its layout.</div>;
  }

  return (
    <div className="p-6 max-w-6xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-primary" />
          Room layout in the lesson player
        </Label>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {inheriting ? 'Using the default lesson layout' : 'Custom layout for this lesson'}
          </span>
          {!inheriting && !isReadOnly && (
            <Button size="sm" variant="outline" onClick={() => onChange({})}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Use default
            </Button>
          )}
        </div>
      </div>
      {assets.length === 0 && (
        <p className="text-xs text-muted-foreground">
          This lesson has no 3D models yet; the preview shows the panel and desk only.
        </p>
      )}
      <LayoutEditor
        value={value}
        onChange={onChange}
        inherited={globalDefault}
        assets={assets}
        readOnly={isReadOnly}
        room={{
          subject: bundle?.chapter?.subject ?? '',
          classNumber: classNumberOf(bundle?.chapter?.class ?? bundle?.chapter?.class_name),
          chapterId: chapterId ?? '',
          topicId: topicId ?? '',
          topicIndex: topicIndex !== undefined && topicIndex >= 0 ? topicIndex : 0,
        }}
      />
    </div>
  );
}
